// Dev-only override: lets `?marketplaceSandboxId=<id>` repoint the slide-type
// marketplace at a per-branch / per-commit Cloudflare Pages sandbox, so ONE
// audience sandbox build can be aimed at any marketplace deploy without a
// rebuild. Counterpart to the presenter app's src/utils/resolveMarketplaceBase.js
// — same param, same id shape, same gate rationale.
//
// This swaps only the ORIGIN of the configured marketplace API URL, preserving
// its path (`/api/slide-types-audience`). That endpoint ships a RELATIVE
// `audienceUrl` which embed-app.module absolutizes against the same origin, so
// repointing here moves both the catalogue and the audience plugin iframes.
//
// Gated on the BUILD, via the audience domain the build was compiled with.
// `VUE_APP_ENVAPP` cannot be used: it does not distinguish these. `VUE_APP_AUDIENCERURL`
// can — the override is honoured on the dev-facing builds a tester opens, inert
// on production:
//   .env.local      → localhost                      (override on)
//   .env.staging    → audience.dev.ahaslide.com      (override on)
//   .env.sandbox    → audience.sandbox.ahaslide.com  (override on)
//   .env.production → audience.ahaslides.com         (inert)
//
// The audience app is opened on a SEPARATE device via the presenter's QR code.
// The presenter stamps ?marketplaceSandboxId= onto that QR / join link, so the
// param arrives on the URL. Strictly URL-driven: the override is in effect ONLY
// while the param is present — deliberately NOT persisted (no localStorage), so
// it can't leak onto unrelated sessions/tabs. Param on the URL ⇒ sandbox;
// no param ⇒ the default marketplace.

// Cloudflare Pages sandbox id: lowercase alphanumerics + dashes. Deliberately
// strict — this value becomes an iframe origin, so anything that could smuggle
// in a host ('.', '/', ':', '@') must not survive validation.
const SANDBOX_ID_REGEX = /^[a-z0-9][a-z0-9-]{0,62}$/

// Audience domains where ?marketplaceSandboxId= is honoured — the dev-facing
// builds. Production hosts (audience.ahaslides.com, and the EU variant) are
// deliberately absent. A per-commit sandbox build is a subdomain of the sandbox
// host, matched by the subdomain check.
const OVERRIDE_HOSTS = ['audience.dev.ahaslide.com', 'audience.sandbox.ahaslide.com']
const LOCAL_HOSTS = ['localhost', '127.0.0.1']

const sandboxOriginFor = (id) => `https://${id}.aha-slide-types-creator.pages.dev`

// True on the dev-facing builds (local, staging, sandbox), false on production.
// Parsed as a URL (not substring-matched) so a lookalike domain can't satisfy
// the check; the endsWith arm only accepts genuine subdomains of an allowed host.
const isOverrideAllowed = (audienceUrl) => {
  try {
    const { hostname } = new URL(audienceUrl)
    if (LOCAL_HOSTS.includes(hostname)) return true
    return OVERRIDE_HOSTS.some((h) => hostname === h || hostname.endsWith(`.${h}`))
  } catch (e) {
    return false
  }
}

/**
 * Resolve the marketplace API URL for this page load. Read straight from the URL
 * each call; nothing is stored between calls.
 *
 * Param on the URL ⇒ that sandbox's origin; no (or invalid, or empty) param ⇒
 * the build-time default.
 *
 * @param {string} defaultUrl  Config.slideTypesMarketplaceUrl
 * @param {string} audienceUrl Config.audienceUrl — the build gate
 * @param {Location} location
 * @returns {string} the marketplace API URL, origin possibly swapped
 */
export function resolveMarketplaceApiUrl(defaultUrl, audienceUrl, location = window.location) {
  if (!isOverrideAllowed(audienceUrl)) return defaultUrl

  const id = (new URLSearchParams(location.search).get('marketplaceSandboxId') || '').trim()
  if (!SANDBOX_ID_REGEX.test(id)) return defaultUrl

  // Swap the origin, keep the path (`/api/slide-types-audience`).
  let resolved
  try {
    const url = new URL(defaultUrl)
    resolved = `${sandboxOriginFor(id)}${url.pathname}${url.search}`
  } catch (e) {
    return defaultUrl
  }

  // eslint-disable-next-line no-console
  console.warn(`[marketplace override] Using sandbox catalogue ${resolved}`)
  return resolved
}

export default resolveMarketplaceApiUrl
